import {
  isRequired,
  isDateLoose,
  type Validation,
  type ValidationResult,
} from './utils';
import {
  formatDate,
  addDaysInTZ,
  startOfDayInTZ,
  calcMinWearDate,
} from '../utils';
import { CART_ITEM_TYPES } from '../constants';

interface OrderVariantRef {
  variant: string;
  inventoryOnly?: boolean;
}

interface OrderFormValues {
  [key: string]: unknown;
  size?: string;
  color?: string;
  variants?: OrderVariantRef[];
  fromInventory?: boolean;
  inStock?: boolean;
  wearDate?: string | number | Date;
  availableDate?: string | number | Date;
  shippingWindowInWeeks?: number;
  shippingDelayInWeeks?: number;
  // market timezone the wear-date day boundary is computed in; callers inject
  // it (client: appConfig state, server: fetchAppConfig) so both sides agree
  timeZone?: string | undefined;
  // extra days the wear date must fall after the estimated ship date; callers
  // inject it from appConfig (see getWearDateBufferDays)
  wearDateBufferDays?: number;
}

// Structured wear-date failure: callers that face the shopper (product page,
// checkout) translate it client-side; `formatWearDateError` keeps the English
// sentence for server logs, admin flows and API messages.
export interface WearDateError {
  code: 'invalidFormat' | 'beforeShipDate' | 'bufferTooShort';
  days?: number;
  // ISO instants; render them as calendar dates in `timeZone`
  shipDate?: string;
  minDate?: string;
  timeZone?: string | undefined;
}

function getWearDateError(order: OrderFormValues): WearDateError | null {
  const {
    wearDate,
    timeZone,
    wearDateBufferDays = 0,
  } = order;

  if (!isDateLoose(wearDate)) {
    return { code: 'invalidFormat' };
  }

  const shipDate = calcMinWearDate(order);
  // compare against the start of the market day: in-stock ship dates are "now",
  // and picked wear dates are anchored to market noon, so a time-of-day
  // comparison would reject the minimum day itself after noon
  const minDate = startOfDayInTZ(timeZone, addDaysInTZ(timeZone, shipDate, wearDateBufferDays));
  if (new Date(wearDate as string | number | Date) < new Date(minDate)) {
    return {
      code: wearDateBufferDays > 0 ? 'bufferTooShort' : 'beforeShipDate',
      days: wearDateBufferDays,
      shipDate: new Date(shipDate).toISOString(),
      minDate: new Date(minDate).toISOString(),
      timeZone,
    };
  }

  return null;
}

function formatWearDateError(err: WearDateError): string {
  const { code, days = 0, timeZone } = err;
  const shipDate = formatDate(err.shipDate!, '', { timeZone });
  const minDate = formatDate(err.minDate!, '', { timeZone });
  if (code === 'invalidFormat') {
    return 'wear date format is invalid';
  }
  if (code === 'bufferTooShort') {
    return `The wear date must be at least ${days} ${days === 1 ? 'day' : 'days'} after the estimated ship date (${shipDate}). The wear date must be on or after ${minDate}.`;
  }
  return `The wear date must be later than the ship date. And please allow an extra 7-10 days for the shipment. The wear date must be later than ${minDate}.`;
}

function isValidWearDate(order: OrderFormValues): ValidationResult {
  const err = getWearDateError(order);
  return err ? { wearDate: formatWearDateError(err) } : false;
}

// Color / size are never pre-selected in the UI, so the customer has to pick
// both before the item is addable. Shared with the inventory (ADD_INVENTORY)
// form, which skips the rest of the order rules but still needs a variant.
const variantSelectionValidations: Validation<OrderFormValues>[] = [
  ({ color }) => isRequired(color) || { color: 'Please select a color.' },
  ({ size }) => isRequired(size) || { size: 'Please select a size.' },
];

const validations: Validation<OrderFormValues>[] = [
  ...variantSelectionValidations,
  ({ poNumber, type }) => (type === CART_ITEM_TYPES.ADD_INVENTORY)
    || isRequired(poNumber)
    || { poNumber: 'PO number is required.' },
  (order) => isValidWearDate(order),
  ({
    size,
    color,
    variants,
    fromInventory,
    type,
  }) => {
    if (type === CART_ITEM_TYPES.ADD_INVENTORY) {
      return true;
    }

    if (fromInventory) {
      return true;
    }

    if (!variants) {
      return true;
    }

    const inventoryOnly = variants.filter((v) => v.inventoryOnly);
    const failed = inventoryOnly.some((io) => io.variant === `${size} / ${color}`);

    if (failed) {
      return { size: 'Invalid size / color' };
    }

    return true;
  },
];

const KEYS = [
  'size',
  'color',
  'poNumber',
];

export {
  validations,
  variantSelectionValidations,
  KEYS,

  isValidWearDate,
  getWearDateError,
  formatWearDateError,
};
